import { getOrCreateDeviceId } from "@/app/_module/services/auth";
import { getApiUrl } from "@/app/_module/services/config";

/**
 * Adds `X-Device-Id` to every browser `fetch()` aimed at our backend.
 *
 * WHY THIS EXISTS
 * The shared axios instance has always sent this header, but 27 files call the
 * backend with raw `fetch()` instead and therefore sent nothing. That was
 * invisible until the MFA API gate shipped: the gate treats a *missing* device
 * id from an MFA-enabled account as a denial (it must — if an absent header
 * meant "skip the check", an attacker would simply omit it), so those 27 call
 * sites started returning 403 MFA_REQUIRED for exactly the users the gate is
 * meant to protect.
 *
 * Patching the 27 call sites by hand was the obvious move and the wrong one:
 * it is 27 chances to miss one, and every future `fetch()` re-opens the hole.
 * One wrapper at the boundary covers all of them and anything written later.
 *
 *      component ──► fetch() ──► [this wrapper] ──► backend
 *                                      │
 *                          adds X-Device-Id if the URL is ours
 *                          and the caller did not set one
 *
 * NOT A SECURITY CONTROL. The device id is client-supplied and always was;
 * this only stops well-behaved callers from being denied for omitting it. The
 * real fix is keying the gate on the JWT's session id, which no caller can
 * omit or forge — see the security audit note.
 *
 * Scoped to our own origin on purpose: attaching an identifier to third-party
 * requests (Stripe, Supabase, PostHog, Google) would leak it off-site.
 */

let installed = false;

/** Backend origin, resolved once. Empty string if it is not configured yet. */
const backendOrigin = (): string => {
  try {
    const url = getApiUrl("/");
    if (!url) return "";
    return new URL(url, window.location.href).origin;
  } catch {
    return "";
  }
};

/** Is this request going to our own API (absolute backend URL, or a relative /api path)? */
const isOurApi = (rawUrl: string, origin: string): boolean => {
  try {
    // Relative URLs resolve against the page, which is our own origin anyway.
    const target = new URL(rawUrl, window.location.href);
    if (origin && target.origin === origin) return true;
    // Same-origin Next.js route handlers that proxy to the backend.
    return target.origin === window.location.origin && target.pathname.startsWith("/api/");
  } catch {
    return false;
  }
};

export function installDeviceHeaderFetch(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  const origin = backendOrigin();

  window.fetch = function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    try {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input?.url;

      if (!url || !isOurApi(url, origin)) return originalFetch(input, init);

      const deviceId = getOrCreateDeviceId();
      if (!deviceId) return originalFetch(input, init);

      // A Request object carries its own headers; init.headers would be ignored
      // for it, so the header has to be merged into a rebuilt Request instead.
      if (input instanceof Request && !init) {
        if (input.headers.has("X-Device-Id")) return originalFetch(input);
        const request = new Request(input);
        request.headers.set("X-Device-Id", deviceId);
        return originalFetch(request);
      }

      const headers = new Headers(
        (init?.headers as HeadersInit | undefined) ??
          (input instanceof Request ? input.headers : undefined),
      );
      // Never overwrite a caller that set it deliberately.
      if (!headers.has("X-Device-Id")) headers.set("X-Device-Id", deviceId);

      return originalFetch(input, { ...init, headers });
    } catch {
      // A bug in this wrapper must never take the app's networking down with it.
      return originalFetch(input, init);
    }
  } as typeof window.fetch;
}
